import React, { useState } from 'react';
import './ProgressTab.css';

/* ════════════════════════════════════════════════════
   Pipeline Steps
   ════════════════════════════════════════════════════ */

interface StepDetail {
  label: string;
  value: string;
}

interface Step {
  num: number;
  title: string;
  script: string;
  status: 'done' | 'pending';
  desc: string;
  how: string;
  stats: StepDetail[];
  inputs: string[];
  outputs: string[];
  checks: string[];
}

const STEPS: Step[] = [
  {
    num: 1,
    title: 'Gap Filling',
    script: 'clean_binance_ohlcv.py',
    status: 'done',
    desc: 'Raw 5m OHLCV data contains timestamp gaps from exchange downtime, API errors, and early-history sparsity. These must be filled before resampling — otherwise aggregated bars (1h, 4h, 1d) would silently compress unequal numbers of 5m bars.',
    how: 'Sort by open_time → generate complete 5min date_range from min to max → left-merge original data (gaps become NaN) → Phase 1: fill from OKX where timestamps match → Phase 2: forward-fill remaining OHLC, zero-fill volume → compute close_time for filled bars → hard assert zero remaining gaps.',
    stats: [
      { label: 'Gaps found', value: '142 across 23 coins' },
      {
        label: 'Fill priority',
        value: 'OKX cross-exchange (real prices) → forward-fill (synthetic)',
      },
      {
        label: 'Volume on synthetic bars',
        value:
          'Zeroed (volume, quote_asset_volume, num_trades, taker_buy_base_volume, taker_buy_quote_volume)',
      },
      {
        label: 'New metadata columns',
        value: "is_filled (bool), fill_source ('binance' | 'okx' | 'ffill')",
      },
      {
        label: 'OKX coverage',
        value: '29/30 coins (EOS has no OKX spot file)',
      },
    ],
    inputs: [
      'dataset/binance/{coin}/spot_ohlcv.parquet',
      'dataset/binance/{coin}/futures_ohlcv.parquet',
      'dataset/okx/spot/{SYM}-USDT_5m.parquet',
    ],
    outputs: [
      'dataset/binance/{coin}/spot_ohlcv_clean.parquet',
      'dataset/binance/{coin}/futures_ohlcv_clean.parquet',
    ],
    checks: [
      'Zero NaN rows after fill (hard assertion)',
      'is_filled=True count matches total inserted rows',
      'fill_source distribution logged per coin',
    ],
  },
  {
    num: 2,
    title: 'Anomalous Spike Detection',
    script: 'detect_anomalous_spikes.py',
    status: 'done',
    desc: 'Scan gap-filled data for flash wicks and anomalous price spikes that indicate exchange system issues (not real market moves). Produces a severity-ranked anomaly report without modifying any data.',
    how: 'For each 5m bar, compute upper_wick%, lower_wick%, bar_return%, and range_multiplier vs rolling-50-bar median range. Any bar exceeding thresholds is flagged. Flagged bars are then classified as flash wicks if the subsequent 1–2 bars show ≥ 50% snap-back reversion.',
    stats: [
      {
        label: 'Upper wick threshold',
        value: '(high − body_top) / close > 5%',
      },
      { label: 'Lower wick threshold', value: '(body_bot − low) / close > 5%' },
      { label: 'Bar return threshold', value: '|close/open − 1| > 5%' },
      {
        label: 'Range multiplier threshold',
        value: 'Bar range > 10× rolling-50-bar median',
      },
      {
        label: 'Flash wick classification',
        value: 'snap_back_ratio ≥ 0.50 within 1–2 bars',
      },
    ],
    inputs: [
      'dataset/binance/{coin}/spot_ohlcv_clean.parquet',
      'dataset/binance/{coin}/futures_ohlcv_clean.parquet',
    ],
    outputs: ['reports/anomalous_spikes.json'],
    checks: [
      'Legitimate large moves (LUNA crash, FTX collapse) are NOT flagged as flash wicks',
      'Price jumps > 50% consecutive close change flagged as WARNING only, not cleaned',
      'Flat/zero-volume bars and volume spikes are excluded from detection',
    ],
  },
  {
    num: 3,
    title: 'Spike Cleaning',
    script: 'clean_anomalous_spikes.py',
    status: 'done',
    desc: 'Read the anomaly report and clamp the high/low of identified flash wicks to body ± 0.1%. The body (open/close) is never modified. Produces two separate output databases: binance_cleaned (spikes clamped) and binance_spike (spikes preserved for research).',
    how: 'For each flagged bar: body_high = max(open, close), body_low = min(open, close), buffer = body_high × 0.001. new_high = min(original_high, body_high + buffer), new_low = max(original_low, body_low − buffer). Write both clamped and unclamped versions.',
    stats: [
      {
        label: 'Clamping formula',
        value: 'high/low pulled to body ± 0.1% buffer',
      },
      { label: 'Flash wicks', value: 'is_flash_wick == True → clamp' },
      {
        label: 'Listing-day spikes',
        value: 'severity_pct > 100 and not flash wick → clamp',
      },
      {
        label: 'Original files',
        value: 'Never modified — raw parquet is source of truth',
      },
    ],
    inputs: [
      'reports/anomalous_spikes.json',
      'dataset/binance/{coin}/spot_ohlcv_clean.parquet',
      'dataset/binance/{coin}/futures_ohlcv_clean.parquet',
    ],
    outputs: [
      'dataset/binance_cleaned/{coin}/spot_ohlcv_clean.parquet  (spikes clamped)',
      'dataset/binance_cleaned/{coin}/futures_ohlcv_clean.parquet',
      'dataset/binance/{coin}/ files unchanged  (spikes preserved → binance_spike DB)',
    ],
    checks: [
      'Clamped bars: new_high ≤ original_high, new_low ≥ original_low',
      'Open/close values unchanged on all bars',
      'binance_spike and binance_cleaned have identical row counts',
    ],
  },
  {
    num: 4,
    title: 'Resampling',
    script: 'resample_binance_ohlcv.py + resample_binance_features.py',
    status: 'done',
    desc: 'Resample the 5m cleaned base data into 5 higher timeframes (15m, 30m, 1h, 4h, 1d) using pandas resample. Then normalize all feature columns from camelCase to snake_case and resample features with type-specific aggregation rules.',
    how: 'OHLCV: load_ohlcv() picks _clean.parquet if available, falls back to raw. Resample with O=first, H=max, L=min, C=last, V=sum, is_filled=max. Features: rename columns camelCase→snake_case, then aggregate: last (OI, L/S ratios), sum (taker volumes, funding rate), recompute (buy/sell ratio = buy_vol / sell_vol after summing).',
    stats: [
      {
        label: 'Timeframes produced',
        value:
          '15m (~35,040/yr), 30m (~17,520/yr), 1h (~8,760/yr), 4h (~2,190/yr), 1d (~365/yr)',
      },
      {
        label: 'OHLCV aggregation',
        value: 'O=first, H=max, L=min, C=last, V=sum',
      },
      {
        label: 'Snapshot features (last)',
        value:
          'open_interest, global_ls_ratio, top_ls_account, top_ls_position',
      },
      {
        label: 'Flow features (sum)',
        value: 'taker buy/sell volumes, funding_rate',
      },
      {
        label: 'Recomputed features',
        value: 'buy_sell_ratio = buy_vol / sell_vol (not averaged)',
      },
      {
        label: 'is_filled propagation',
        value: 'True if ANY 5m bar in the window was synthetic',
      },
      {
        label: 'fill_source propagation',
        value: 'Worst-case: ffill > okx > binance',
      },
      { label: 'Funding rate', value: '8h native → only upsamples to 1d' },
    ],
    inputs: [
      'dataset/binance_cleaned/{coin}/*_clean.parquet',
      'dataset/binance/{coin}/*.parquet  (features: OI, L/S, taker, funding)',
    ],
    outputs: [
      'dataset/binance/{coin}/spot_ohlcv_{15m,30m,1h,4h,1d}.parquet',
      'dataset/binance/{coin}/futures_ohlcv_{15m,30m,1h,4h,1d}.parquet',
      'dataset/binance/{coin}/{feature}_{clean,15m,30m,1h,4h,1d}.parquet',
    ],
    checks: [
      'Row count ratios match expected (e.g., 1h has ~12× fewer rows than 5m)',
      'No NaN in OHLCV columns after resample',
      'is_filled column present in all output files',
    ],
  },
  {
    num: 5,
    title: 'Validation',
    script: 'validate_binance_ohlcv.py',
    status: 'done',
    desc: 'Comprehensive validation pass across all 30 coins, both markets (spot + futures), and all 6 timeframes. Checks structural integrity, OHLCV constraints, anomalies, completeness, cross-validation of resampled data, and spot-futures correlation.',
    how: 'Run all checks per coin/market/timeframe combination. Each check produces a severity (CRITICAL, WARNING, INFO). Pipeline target: 0 CRITICAL issues and 0 gap WARNINGs after the full cleaning pipeline.',
    stats: [
      {
        label: 'Structural checks',
        value:
          'Duplicate timestamps, non-monotonic timestamps, NaN in OHLCV, NaN in auxiliary',
      },
      {
        label: 'OHLCV integrity',
        value:
          'high ≥ low, high ≥ open/close, low ≤ open/close, all prices > 0, volume ≥ 0',
      },
      {
        label: 'Anomaly checks',
        value:
          'Close-to-close jump > 50% (WARNING), ≥ 12 consecutive flat bars (WARNING), volume spike > 10× median (INFO)',
      },
      {
        label: 'Completeness',
        value: 'Coverage < 90% → CRITICAL, < 99% → WARNING',
      },
      {
        label: 'Cross-validation',
        value:
          '100 random bars: resampled vs re-aggregated from 5m source (mismatch → CRITICAL)',
      },
      {
        label: 'Spot-futures correlation',
        value:
          'Pearson r < 0.95 → WARNING, < 0.80 → CRITICAL. Extreme basis > 5% → INFO',
      },
    ],
    inputs: ['All cleaned and resampled parquet files across all coins'],
    outputs: [
      'Validation report: per-coin/market/timeframe severity breakdown',
    ],
    checks: [
      'Target: 0 CRITICAL issues',
      'Target: 0 gap WARNINGs (after gap-fill)',
      'is_filled column present in all cleaned and resampled files',
    ],
  },
];

/* ════════════════════════════════════════════════════
   Coin Universe
   ════════════════════════════════════════════════════ */

const COIN_GROUPS = [
  {
    label: 'Layer 1 / Base Chains',
    coins: [
      'BTC',
      'ETH',
      'SOL',
      'BNB',
      'XRP',
      'ADA',
      'AVAX',
      'DOT',
      'LTC',
      'BCH',
      'XLM',
      'ETC',
      'TRX',
    ],
    color: '#58a6ff',
  },
  {
    label: 'DeFi',
    coins: ['LINK', 'AAVE', 'UNI', 'PENDLE', 'LDO', 'ENA'],
    color: '#3fb950',
  },
  {
    label: 'AI / Agent',
    coins: ['TAO', 'RENDER', 'FET', 'WLD', 'ICP', 'GRT'],
    color: '#bc8cff',
  },
  {
    label: 'Layer 2 / Scaling',
    coins: ['ARB', 'OP', 'STX', 'IMX', 'MANTA'],
    color: '#d29922',
  },
];

/* ════════════════════════════════════════════════════
   What's Done
   ════════════════════════════════════════════════════ */

interface DoneItem {
  text: string;
}

interface DoneCategory {
  title: string;
  color: string;
  items: DoneItem[];
}

const DONE: DoneCategory[] = [
  {
    title: 'Data Pipeline',
    color: '#3fb950',
    items: [
      {
        text: 'Full 5-step pipeline operational: gap-fill → spike detect → spike clean → resample → validate',
      },
      {
        text: '30 coins tracked across 5 categories (L1, DeFi, AI, L2, Scaling) — all with futures + OKX cross-listing',
      },
      {
        text: '8 data types collected: Spot OHLCV, Futures OHLCV, Open Interest, Global L/S Ratio, Top L/S (Account), Top L/S (Position), Taker Buy/Sell, Funding Rate',
      },
      {
        text: '6 timeframes generated per coin per market: 5m (base), 15m, 30m, 1h, 4h, 1d',
      },
      {
        text: '142 gaps detected and filled across 23 coins using OKX cross-exchange data + forward-fill',
      },
      {
        text: 'Flash wick detection and clamping with body ± 0.1% buffer — preserves legitimate large moves',
      },
      {
        text: 'Full validation suite: structural integrity, OHLCV constraints, coverage, cross-validation, spot-futures correlation',
      },
      {
        text: 'is_filled / fill_source metadata propagation through resampling for downstream model awareness',
      },
    ],
  },
  {
    title: 'ClickHouse Storage',
    color: '#58a6ff',
    items: [
      {
        text: '4 databases: binance_raw (10 tables), binance_preprocessed (6 tables), binance_cleaned (2 tables + 6 views), binance_spike (2 tables + 6 views)',
      },
      {
        text: 'ReplacingMergeTree engine with (symbol, open_time) dedup key across all tables',
      },
      {
        text: 'Feature tables in cleaned & spike are SQL VIEWs pointing to preprocessed — single source of truth for derivatives',
      },
      { text: 'Read-only analyst user configured for dashboard/API access' },
    ],
  },
  {
    title: 'Ingestion & Automation',
    color: '#d29922',
    items: [
      {
        text: 'Manual onboarding pipeline: pull_coins.py → auto-register in scrape_binance.py → pipeline_process_coin.py',
      },
      {
        text: 'Daily cron (1 AM UTC): scrape_binance.py --daily → aggregate_1m_to_5m.py → pipeline_process_coin.py',
      },
      {
        text: '1m OHLCV rolling accumulation — becomes primary base over time; historical 5m retained for pre-2-week data',
      },
      {
        text: 'SSE-based live log streaming for coin onboarding progress in the dashboard',
      },
    ],
  },
  {
    title: 'Dashboard Frontend',
    color: '#bc8cff',
    items: [
      {
        text: 'Add Tab: batch coin onboarding with Binance validation, 6-step progress stepper, live SSE logs, quick-add popular coins, duplicate detection',
      },
      {
        text: 'Delete Tab: interactive heatmap (coverage per coin per hour), canvas candlestick + volume charts, feature line charts, 4 database views, 60s auto-refresh',
      },
      {
        text: 'Edit Tab: Parquet export workflow with create/edit/delete configs, multi-select coins (52), 8 data types, ZIP download, presets with localStorage persistence',
      },
      {
        text: 'Search Tab: API and database health checks with 15s polling and animated status indicators',
      },
      {
        text: 'Architecture reference: full database schema, pipeline flow diagrams, processing detail, access control documentation',
      },
    ],
  },
  {
    title: 'API Backend',
    color: '#3fb950',
    items: [
      {
        text: 'FastAPI server with 5 endpoints: POST /data (JSON), POST /data/parquet (ZIP stream), GET /data (shareable URLs), GET /data/health, GET /data/databases',
      },
      {
        text: 'Parameterized SQL query builder — injection-safe with column prefixing for derivative merges',
      },
      {
        text: 'Input validation on coins, timeframes, data types, date ranges with structured error responses',
      },
      {
        text: 'Per-coin ZIP structure: {COIN}/spot_ohlcv.parquet + {COIN}/derivatives.parquet with column name-spacing',
      },
    ],
  },
];

/* ════════════════════════════════════════════════════
   What's Next
   ════════════════════════════════════════════════════ */

interface TodoItem {
  name: string;
  desc: string;
  priority: 'high' | 'medium' | 'low';
}

const NEXT_ITEMS: TodoItem[] = [
  {
    name: 'Search tab — full-text data search',
    desc: 'Build a query interface to search across all databases, coins, timeframes. Support filters by date range, coin, data type. Currently the Search tab only shows API/DB health status.',
    priority: 'high',
  },
  {
    name: 'Search tab — query builder',
    desc: 'Visual SQL-like query builder for constructing ClickHouse queries without writing raw SQL. Should support joins across OHLCV and feature tables.',
    priority: 'high',
  },
  {
    name: 'Auth & access control',
    desc: "Add authentication to FastAPI endpoints. Currently all routes are open. Implement API key or JWT-based auth. The ClickHouse analyst user (read-only) already exists but isn't enforced at the API layer.",
    priority: 'high',
  },
  {
    name: 'Production CORS lockdown',
    desc: 'CORS is currently wide open (allow all origins). Lock down to specific frontend domain(s) before production deployment.',
    priority: 'high',
  },
  {
    name: 'Search result export',
    desc: 'Allow exporting search results as CSV or Parquet from the Search tab. The Edit tab already has Parquet/ZIP export — can reuse the backend endpoint.',
    priority: 'medium',
  },
  {
    name: 'Delete tab — wire bulk delete to backend',
    desc: 'The Delete tab heatmap UI is fully built, but bulk delete actions are frontend-only. Need a DELETE endpoint on the API and confirmation flow.',
    priority: 'medium',
  },
  {
    name: 'Trade CRUD integration',
    desc: 'The Trade type (id, symbol, side, quantity, price, strategy, status, notes) and useTrades hook are defined but completely unused in the UI. Wire into a trade journal or portfolio tracker.',
    priority: 'medium',
  },
  {
    name: 'Automate pipeline re-run on coin addition',
    desc: 'When new coins are onboarded via the Add tab, automatically trigger the full 5-step pipeline (gap-fill → detect → clean → resample → validate) instead of requiring manual script execution.',
    priority: 'medium',
  },
  {
    name: 'Validation alerting in daily cron',
    desc: 'The daily pipeline (scrape → aggregate → process) runs via cron at 1 AM UTC but has no alerting. If validation fails (CRITICAL issues), send a notification.',
    priority: 'medium',
  },
  {
    name: 'Architecture sub-tab content',
    desc: "The Architecture sub-tab component exists in the Add tab with full database schema and pipeline flow diagrams, but it's not fully surfaced to users.",
    priority: 'low',
  },
  {
    name: 'Derivative data backfill',
    desc: 'Open interest, L/S ratios, and taker data are limited to a ~27-day rolling window from Binance. Investigate alternative sources or caching strategies to extend historical coverage.',
    priority: 'low',
  },
];

const PRIORITY_COLOR: Record<string, string> = {
  high: '#f85149',
  medium: '#d29922',
  low: '#8b949e',
};
const PRIORITY_BG: Record<string, string> = {
  high: 'rgba(248,81,73,0.1)',
  medium: 'rgba(210,153,34,0.1)',
  low: 'rgba(139,148,158,0.08)',
};

/* ════════════════════════════════════════════════════
   Component
   ════════════════════════════════════════════════════ */

function ProgressTab() {
  const [expandedStep, setExpandedStep] = useState<number | null>(null);
  const doneSteps = STEPS.filter((s) => s.status === 'done').length;
  const totalCoins = COIN_GROUPS.reduce((s, g) => s + g.coins.length, 0);
  const totalDone = DONE.reduce((s, c) => s + c.items.length, 0);

  const toggleStep = (num: number) =>
    setExpandedStep(expandedStep === num ? null : num);

  return (
    <div className="pg-container">
      {/* ── Summary row ── */}
      <div className="pg-summary-row">
        <div className="pg-summary-card">
          <div className="pg-big-num" style={{ color: '#3fb950' }}>
            {doneSteps}/{STEPS.length}
          </div>
          <div className="pg-label">Pipeline Steps Done</div>
          <div className="pg-overall-bar-wrap">
            <div
              className="pg-overall-bar"
              style={{
                width: `${(doneSteps / STEPS.length) * 100}%`,
                background: 'linear-gradient(90deg, #238636, #3fb950)',
              }}
            />
          </div>
        </div>
        <div className="pg-summary-card">
          <div className="pg-big-num" style={{ color: '#58a6ff' }}>
            {totalCoins}
          </div>
          <div className="pg-label">Coins Tracked</div>
        </div>
        <div className="pg-summary-card">
          <div className="pg-big-num" style={{ color: '#bc8cff' }}>
            {totalDone}
          </div>
          <div className="pg-label">Milestones Done</div>
        </div>
        <div className="pg-summary-card">
          <div className="pg-big-num" style={{ color: '#d29922' }}>
            {NEXT_ITEMS.length}
          </div>
          <div className="pg-label">Items Remaining</div>
        </div>
        <div className="pg-summary-card">
          <div className="pg-big-num" style={{ color: '#f85149' }}>
            {NEXT_ITEMS.filter((i) => i.priority === 'high').length}
          </div>
          <div className="pg-label">High Priority</div>
        </div>
      </div>

      {/* ── Coin Universe ── */}
      <div className="pg-section">
        <div className="pg-section-title">Coin Universe</div>
        <div className="pg-coin-groups">
          {COIN_GROUPS.map((g) => (
            <div key={g.label} className="pg-coin-group">
              <div className="pg-coin-group-label" style={{ color: g.color }}>
                {g.label}
              </div>
              <div className="pg-coin-chips">
                {g.coins.map((c) => (
                  <span
                    key={c}
                    className="pg-coin-chip"
                    style={{ borderColor: g.color + '44', color: g.color }}
                  >
                    {c}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="pg-coin-note">
          All 30 coins have Binance futures markets + OKX cross-listing for
          gap-fill validation.
        </div>
      </div>

      {/* ── Pipeline Steps ── */}
      <div className="pg-section">
        <div className="pg-section-title">Pipeline Steps</div>
        <div className="pg-pipeline">
          {STEPS.map((step, i) => {
            const isOpen = expandedStep === step.num;
            return (
              <div key={step.num} className="pg-step">
                <div className="pg-step-left">
                  <div className={`pg-step-num ${step.status}`}>{step.num}</div>
                  {i < STEPS.length - 1 && (
                    <div className={`pg-step-line ${step.status}`} />
                  )}
                </div>
                <div className="pg-step-body">
                  <button
                    className="pg-step-toggle"
                    onClick={() => toggleStep(step.num)}
                  >
                    <div className="pg-step-header">
                      <span className="pg-step-title">{step.title}</span>
                      <span
                        className={`pg-badge ${step.status === 'done' ? 'complete' : 'in-progress'}`}
                      >
                        {step.status === 'done' ? 'Complete' : 'In Progress'}
                      </span>
                      <span className="pg-step-arrow">
                        {isOpen ? '▾' : '▸'}
                      </span>
                    </div>
                    <div className="pg-step-script">{step.script}</div>
                    <div className="pg-step-desc">{step.desc}</div>
                  </button>

                  {isOpen && (
                    <div className="pg-step-expanded">
                      <div className="pg-step-block">
                        <div className="pg-step-block-title">How it works</div>
                        <div className="pg-step-block-text">{step.how}</div>
                      </div>
                      <div className="pg-step-block">
                        <div className="pg-step-block-title">Key details</div>
                        <div className="pg-kv-list">
                          {step.stats.map((s, j) => (
                            <div key={j} className="pg-kv">
                              <span className="pg-kv-label">{s.label}</span>
                              <span className="pg-kv-value">{s.value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="pg-step-block">
                        <div className="pg-step-block-title">Inputs</div>
                        {step.inputs.map((inp, j) => (
                          <div key={j} className="pg-file-path">
                            {inp}
                          </div>
                        ))}
                      </div>
                      <div className="pg-step-block">
                        <div className="pg-step-block-title">Outputs</div>
                        {step.outputs.map((out, j) => (
                          <div key={j} className="pg-file-path">
                            {out}
                          </div>
                        ))}
                      </div>
                      <div className="pg-step-block">
                        <div className="pg-step-block-title">Verification</div>
                        <ul className="pg-check-list">
                          {step.checks.map((c, j) => (
                            <li key={j}>{c}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── What's Done ── */}
      <div className="pg-section">
        <div className="pg-section-header">
          <div className="pg-section-title">What's Done</div>
          <div className="pg-done-count">{totalDone} milestones</div>
        </div>
        <div className="pg-done-grid">
          {DONE.map((cat) => (
            <div key={cat.title} className="pg-done-category">
              <div className="pg-done-cat-header">
                <span
                  className="pg-done-cat-dot"
                  style={{ background: cat.color }}
                />
                <span
                  className="pg-done-cat-title"
                  style={{ color: cat.color }}
                >
                  {cat.title}
                </span>
                <span className="pg-done-cat-count">{cat.items.length}</span>
              </div>
              <ul className="pg-done-list">
                {cat.items.map((item, j) => (
                  <li key={j}>{item.text}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* ── What's Next ── */}
      <div className="pg-section">
        <div className="pg-section-header">
          <div className="pg-section-title">What's Next</div>
          <div className="pg-done-count">{NEXT_ITEMS.length} items</div>
        </div>
        <div className="pg-next-list">
          {NEXT_ITEMS.map((item, i) => (
            <div
              key={i}
              className="pg-next-item"
              style={{
                borderLeftColor: PRIORITY_COLOR[item.priority],
                background: PRIORITY_BG[item.priority],
              }}
            >
              <div className="pg-next-header">
                <span className="pg-next-index">{i + 1}</span>
                <span className="pg-next-name">{item.name}</span>
                <span
                  className="pg-next-priority"
                  style={{
                    color: PRIORITY_COLOR[item.priority],
                    background: PRIORITY_COLOR[item.priority] + '20',
                  }}
                >
                  {item.priority}
                </span>
              </div>
              <div className="pg-next-desc">{item.desc}</div>
            </div>
          ))}
        </div>
        <div className="pg-legend">
          <span className="pg-legend-item">
            <span className="pg-legend-dot" style={{ background: '#f85149' }} />{' '}
            High priority
          </span>
          <span className="pg-legend-item">
            <span className="pg-legend-dot" style={{ background: '#d29922' }} />{' '}
            Medium priority
          </span>
          <span className="pg-legend-item">
            <span className="pg-legend-dot" style={{ background: '#8b949e' }} />{' '}
            Low priority
          </span>
        </div>
      </div>
    </div>
  );
}

export default ProgressTab;
